import React, {
  useMemo,
  useCallback,
  useRef,
  useState,
  useEffect
} from 'react';
import styled from 'styled-components';
import { InputNumber, Slider } from 'antd';
import throttle from 'lodash/throttle';

const isMobile = window.innerWidth < 845;

const SliderPriceRangeContainer = styled.div`
  margin: auto;

  @media (max-width: ${(props) =>
      props.theme.devices.isTablet ? '0px' : '845px'}) {
    width: calc(100vw - 62px);
    padding: 0;
  }

  .title {
    color: #2b3d54;
    font-size: 12px;
    text-align: center;
    font-family: 'Oswald', 'serif';
  }

  .title.label {
    min-width: 150px;
  }

  .ant-input-number-affix-wrapper {
    width: 110px;
  }
`;

type SlidePriceRangeProps = {
  range: number[];
  // eslint-disable-next-line no-unused-vars
  setRange: (values: number[]) => void;
  currency: string;
  decimal?: number;
  limit?: number[];
  label: string;
};

const SliderPriceRange = ({
  range,
  setRange,
  currency,
  label,
  decimal = 0,
  limit = [0, 10000]
}: SlidePriceRangeProps) => {
  const [localRange, setLocalRange] = useState(range);

  useEffect(() => {
    setLocalRange(range);
  }, [range]);

  const throttledSetRange = useRef(
    throttle((values: number[]) => {
      setRange(values);
    }, 300)
  ).current;

  const handleSliderChange = useCallback(
    (values: number[]) => {
      setLocalRange(values);
      throttledSetRange(values);
    },
    [throttledSetRange]
  );

  const displayCurrency = useMemo(() => {
    const defaultNumber: number = 1;
    return defaultNumber
      .toLocaleString('fr-FR', {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: decimal
      })
      .replace(defaultNumber.toString(), '');
  }, [currency, decimal]);

  const marks = useMemo(() => {
    return limit.reduce((acc, item) => {
      if (item !== null && item !== undefined) {
        // @ts-expect-error value
        acc[item.toString()] = item;
      }
      return acc;
    }, {});
  }, [limit]);

  const InputMinValue = () => (
    <div className="title label">
      <InputNumber
        min={limit[0]}
        max={limit[1]}
        prefix={displayCurrency}
        value={localRange[0]}
        step={100}
        onChange={(e) => {
          const newRange = [e as number, localRange[1]];
          setLocalRange(newRange);
          setRange(newRange);
        }}
      />
    </div>
  );

  const InputMaxValue = () => (
    <div className="title label">
      <InputNumber
        min={localRange[0] + 1}
        max={localRange[1]}
        prefix={displayCurrency}
        value={localRange[1]}
        step={100}
        onChange={(e) => {
          const newRange = [localRange[0], e as number];
          setLocalRange(newRange);
          setRange(newRange);
        }}
      />
    </div>
  );

  return (
    <SliderPriceRangeContainer>
      <div className="labelContainer">
        <p className="label">{label}</p>
      </div>
      <div className="slider-container">
        {!isMobile && <InputMinValue />}
        <Slider
          style={{ width: isMobile ? '90%' : 300 }}
          range
          value={localRange}
          min={limit[0]}
          max={limit[1]}
          onChange={handleSliderChange}
        />
        {!isMobile && <InputMaxValue />}
      </div>
    </SliderPriceRangeContainer>
  );
};

export default SliderPriceRange;
